/**
 * 画面いちばん上の「空けておく高さ」（セーフエリア）。
 *
 * 背景: 船員アプリを端末に入れて全画面で使うと、スマホ上部のノッチ／カメラ穴（パンチホール）や
 * 時計・電波表示のバーに画面の中身が潜り込み、ヘッダーの見出しやボタンが隠れてしまう。
 * CSS の env(safe-area-inset-top) は端末やWebViewによっては 0 しか返さず、それだけでは防げない。
 *
 * そこで次の順で高さを決め、CSS変数 --app-safe-top（端末の実px）に入れて全画面で使う。
 *   ① 端末が報告するセーフエリア（env が効く端末はこれが正）
 *   ② 効かない端末は、ホーム画面から起動した全画面表示なら既定値を確保（カメラ穴の分）
 *   ③ さらに端末ごとの微調整（設定画面の「画面上の余白」）を足す
 *
 * ③があるので、どんな端末でも利用者の手元で必ず直せる。
 * ※ #root には文字サイズの zoom がかかるため、CSS 側では倍率で割って使うこと。
 */

const KEY = 'catms_header_offset';

/** 端末ごとの微調整（設定画面で選ぶ）。単位は px。増やす方向だけでなく減らす方向も出す */
export const HEADER_OFFSET_OPTIONS: { value: number; label: string }[] = [
  { value: -16, label: 'せまい' },
  { value: 0, label: '標準' },
  { value: 16, label: '広い' },
  { value: 32, label: 'もっと広い' },
];

/** 端末アプリ（ホーム画面から開いた全画面表示・船員APKのWebView）で確保する高さ。
 *  Android のステータスバーは 24〜32dp、カメラ穴（パンチホール）はさらに大きいことがあり、
 *  iPhone のノッチは 44〜59px。どの端末でも隠れないよう 44px を既定にする。 */
const APP_FALLBACK = 44;
/** ブラウザで開いている場合（ページはステータスバーの下に描かれる）の最低限の余白 */
const BROWSER_MIN = 24;
/** どんな設定でもこれ以下にはしない */
const HARD_MIN = 8;
/** 端末が下のセーフエリアを報告しないアプリ表示で確保する高さ（ジェスチャーバーの保険） */
const APP_BOTTOM_FALLBACK = 10;

export function getHeaderOffset(): number {
  const n = Number(localStorage.getItem(KEY));
  return Number.isFinite(n) ? Math.max(-40, Math.min(80, n)) : 0;
}

export function setHeaderOffset(px: number): void {
  localStorage.setItem(KEY, String(px));
  applySafeArea();
}

/**
 * ブラウザではなく「アプリとして」開いているか。
 * 船員端末はホーム画面のアイコン（PWA/TWA）か、WebView に包んだAPKで開く。
 * どちらも画面いっぱいに描かれるため、ノッチの下敷きになりやすい。
 */
function isAppShell(): boolean {
  try {
    for (const m of ['standalone', 'fullscreen', 'minimal-ui']) {
      if (window.matchMedia(`(display-mode: ${m})`).matches) return true;
    }
  } catch { /* matchMedia が無い環境は無視 */ }
  if ((navigator as unknown as { standalone?: boolean }).standalone === true) return true;
  // Android の WebView に包んだアプリ（船員APK）は UA に "; wv)" が入る
  if (/;\s*wv\)/i.test(navigator.userAgent)) return true;
  return false;
}

/** 端末が報告するセーフエリアの高さ（効かない端末は 0）。上下どちらも測れる */
function readEnvSafe(side: 'top' | 'bottom'): number {
  try {
    const probe = document.createElement('div');
    // body は zoom の外側なので、ここで測った値は端末の実pxになる
    probe.style.cssText =
      `position:fixed;top:0;left:0;width:0;pointer-events:none;visibility:hidden;height:env(safe-area-inset-${side},0px)`;
    document.body.appendChild(probe);
    const h = probe.getBoundingClientRect().height;
    probe.remove();
    return Number.isFinite(h) ? Math.round(h) : 0;
  } catch { return 0; }
}

/** いま確保すべき高さ（px）を計算する */
export function computeSafeTop(): number {
  const env = readEnvSafe('top');
  // env が効く端末でも、報告値が小さすぎてカメラ穴に負けることがあるため既定値と大きい方を採る
  const floor = isAppShell() ? APP_FALLBACK : BROWSER_MIN;
  const base = Math.max(env, floor);
  return Math.max(HARD_MIN, base + getHeaderOffset());
}

/**
 * 画面いちばん下で空けておく高さ。
 * Android のジェスチャーバー／iPhone のホームバーに下のタブが隠れないための余白。
 * 以前は 18px の決め打ちで、しかも文字サイズの zoom で膨らんでいた（文字「大」で約23px）。
 * 端末が報告する値を最優先にし、報告が無いアプリ表示のときだけ控えめな既定値を使う。
 */
export function computeSafeBottom(): number {
  const env = readEnvSafe('bottom');
  if (env > 0) return env;
  return isAppShell() ? APP_BOTTOM_FALLBACK : 0;
}

/** 計算した高さを CSS変数に反映する（アプリ起動時と画面の向きが変わったときに呼ぶ） */
export function applySafeArea(): void {
  const root = document.documentElement;
  root.style.setProperty('--app-safe-top', `${computeSafeTop()}px`);
  root.style.setProperty('--app-safe-bottom', `${computeSafeBottom()}px`);
}

/** 起動時に1回だけ呼ぶ。向き変更・表示領域の変化にも追従させる */
export function initSafeArea(): void {
  applySafeArea();
  window.addEventListener('resize', applySafeArea);
  window.addEventListener('orientationchange', applySafeArea);
}
